import { useTheme } from "@emotion/react";
import { Box, TextField, Typography } from "@mui/material";
import useStyles, { sxCampoCliente, sxCampoClienteComNome } from "./styles";
import TipoClienteChip from "../tipoCliente/TipoClienteChip";
import GrupoChip from "../grupoEconomico/GrupoChip";
import {
  getClienteOptionLabel,
  getNomeCliente,
} from "../grupoEconomico/helpers";

// Um cliente só tem selo quando é um objeto de verdade. O Autocomplete usa ""
// como valor vazio, e nesse caso não há nada a marcar.
function temSelo(option) {
  return Boolean(option) && typeof option === "object";
}

/**
 * Selo de um cliente: PF/PJ, ou o selo de grupo quando for grupo econômico.
 *
 * O grupo econômico é um cliente virtual, sem CNPJ e sem contraparte no ERP —
 * o tipoCliente dele é só o default da coluna. Por isso recebe o selo de grupo
 * e não o de PF/PJ, que seria informação inventada.
 *
 * Usado tanto na lista de opções quanto no campo já preenchido, para que os
 * dois digam a mesma coisa.
 */
export function SeloCliente({ option, sx = {} }) {
  const theme = useTheme();
  const styles = useStyles(theme);

  if (!temSelo(option)) return null;

  const estilo = { ...styles.selo, ...sx };

  return option?.isGrupoEconomico ? (
    <GrupoChip sx={estilo} />
  ) : (
    <TipoClienteChip cliente={option} sx={estilo} />
  );
}

/**
 * Opção de um autocomplete de cliente: nome + selo.
 *
 * Só funciona no VirtualizedAutocomplete por causa do `data-conteudo-rico`:
 * por padrão a lista virtualizada descarta os children do renderOption e
 * exibe o texto derivado da chave (o getOptionLabel). O atributo liga o
 * caminho que de fato renderiza este conteúdo.
 */
export default function OpcaoCliente({ option }) {
  const theme = useTheme();
  const styles = useStyles(theme);

  const nome = getNomeCliente(option);

  return (
    <Box sx={styles.opcao}>
      <Typography
        sx={styles.nome}
      >{`${option?.clienteErpId || "GRUPO"} - ${nome}`}</Typography>
      <SeloCliente option={option} />
    </Box>
  );
}

/**
 * Fábrica pronta para o prop `renderOption` do VirtualizedAutocomplete:
 *
 *   renderOption={renderOpcaoCliente()}
 *
 * Mantém o spread de `props` (que carrega a key usada pela virtualização) e
 * marca a opção como de conteúdo próprio.
 */
export function renderOpcaoCliente() {
  return (props, option) => (
    <li {...props} data-conteudo-rico="true">
      <OpcaoCliente option={option} />
    </li>
  );
}

/**
 * Monta o `endAdornment` do campo com o selo do cliente selecionado à esquerda
 * dos botões de limpar e abrir — o texto do input vem de um <input> e não
 * aceita JSX, então o adorno é o único lugar onde o selo cabe.
 *
 *   sx={sxCampoCliente}
 *   InputProps={{
 *     ...params.InputProps,
 *     endAdornment: endAdornmentCliente(params, clienteSelecionado),
 *   }}
 *
 * Depende do `sxCampoCliente` para funcionar: é ele que tira o adorno do
 * posicionamento absoluto do MUI e deixa o input encolher com reticências.
 *
 * Sem cliente selecionado devolve o adorno original intacto.
 */
export function endAdornmentCliente(params, option) {
  const adornoOriginal = params?.InputProps?.endAdornment;

  if (!temSelo(option)) return adornoOriginal;

  return (
    <>
      <SeloCliente option={option} />
      {adornoOriginal}
    </>
  );
}

/**
 * Campo (renderInput) de um autocomplete de cliente.
 *
 *   renderInput={(params) => (
 *     <CampoCliente
 *       params={params}
 *       option={clienteSelecionado}
 *     />
 *   )}
 *
 * Resolve duas coisas que o <input> nativo não entrega:
 *
 * 1. O selo à direita, antes dos botões de limpar e abrir.
 * 2. Reticências no nome longo em qualquer estado. Em foco o navegador rola o
 *    texto do input para acompanhar o cursor e descarta o text-overflow — por
 *    isso, enquanto ninguém está digitando, o nome é exibido como elemento de
 *    texto comum e o input é colapsado. Basta digitar para o input voltar.
 *
 * A comparação com o rótulo diz se o usuário já digitou: o MUI mantém em
 * `inputProps.value` exatamente o rótulo do item selecionado até a primeira
 * tecla, e o restaura ao sair do campo. Quando o autocomplete usa um
 * getOptionLabel próprio, ele precisa vir em `rotuloDaOpcao`: um rótulo
 * diferente do que está no input nunca casa, e o campo perderia as reticências.
 *
 * `adornoExtra` entra antes do selo, para os campos que já exibem algo ali
 * (o indicador de carregamento das buscas no ERP, por exemplo).
 */
export function CampoCliente({
  params,
  option,
  rotuloDaOpcao,
  adornoExtra = null,
  label = "Cliente",
  sx = {},
  ...props
}) {
  const theme = useTheme();
  const styles = useStyles(theme);

  const rotulo = !temSelo(option)
    ? ""
    : rotuloDaOpcao
      ? rotuloDaOpcao(option)
      : getClienteOptionLabel(option);
  // O texto vazio também conta como "ninguém digitou": o Autocomplete só
  // preenche o inputValue com o rótulo num efeito, então existe um render em
  // que há cliente selecionado e o input ainda está em "". Sem esse caso, o
  // nome voltaria para dentro do input nesse intervalo e as reticências
  // sumiriam. Digitar até esvaziar o campo limpa o cliente, então aqui o ""
  // nunca convive com um cliente que o usuário esteja editando.
  const textoAtual = params?.inputProps?.value ?? "";
  const exibirNome =
    Boolean(rotulo) && (textoAtual === rotulo || textoAtual === "");

  return (
    <TextField
      {...params}
      {...props}
      // O Autocomplete monta `params.size` como `size === "small" ? "small" :
      // undefined`. Um `size="small"` escrito antes do spread seria apagado
      // por esse undefined e o campo renderizaria médio, destoando dos demais
      // filtros da tela. Por isso ele vem depois, e só como padrão.
      size={params?.size ?? "small"}
      label={label}
      sx={{
        ...sxCampoCliente,
        ...(exibirNome ? sxCampoClienteComNome : {}),
        ...sx,
      }}
      InputProps={{
        ...params.InputProps,
        startAdornment: exibirNome ? (
          <Box className="campo-cliente-nome" sx={styles.nomeNoCampo}>
            {rotulo}
          </Box>
        ) : (
          params?.InputProps?.startAdornment
        ),
        endAdornment: (
          <>
            {adornoExtra}
            {endAdornmentCliente(params, option)}
          </>
        ),
      }}
    />
  );
}
